import { useCallback, useRef, useState } from 'react';
/** Brief death-mark flash before the redeem projectile fires. */
export const REDEEM_DEATH_MARK_MS = 380;
/** Max wait for redeem projectile + target hit FX; usually ends sooner via deploy anim complete. */
export const REDEEM_DEATH_EFFECT_WAIT_MS = 1100;
/** Extra beat after projectile lands before redeemer dissipation. */
export const REDEEM_DEATH_EFFECT_POST_HIT_MS = 280;
export const REDEEM_DEATH_FINALE_MS = 420;

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function convertRedeemDeathSequenceForViewer(sequence, viewerUserId) {
  if (!sequence || !viewerUserId) {
    throw new Error('convertRedeemDeathSequenceForViewer: sequence and viewerUserId are required');
  }
  if (!sequence.unitInstanceId) {
    throw new Error('convertRedeemDeathSequenceForViewer: unitInstanceId is required');
  }
  const side = String(sequence.playerId) === String(viewerUserId) ? 'your' : 'opponent';
  if (sequence.zone !== 'front' && sequence.zone !== 'flank') {
    throw new Error(`convertRedeemDeathSequenceForViewer: invalid zone ${String(sequence.zone)}`);
  }
  return {
    ...sequence,
    viewerZoneName: `${side}_${sequence.zone}`,
    viewerIndex: sequence.index,
  };
}

/**
 * Orchestrates the redeem death VFX over an ALREADY-authoritative game state.
 *
 * The server resolves Redeem inline and pushes the final state in one broadcast; this hook never
 * talks back to the server. It marks every dying redeem unit immediately, then plays
 * marked → effect → death strictly one unit at a time. The redeem projectile for each unit is
 * stashed up front (with its target HP held) and only fired during that unit's turn, so simultaneous
 * redeem deaths never show their damage all at once.
 *
 * All visual state is keyed by unit instanceId — never by slot alone.
 */
export function useRedeemDeathSequence({
  viewerUserIdRef,
  redeemMarkedByInstanceIdRef,
  setRedeemMarkedGhostTick,
  suppressSlotDeathInstanceIdsRef,
  completedRedeemDeathInstanceIdsRef,
  clearSlotPreviousStateRef,
  pendingUnitDamageFxSlotsRef,
  queueDamageAnimationTrigger,
  resolveSlotDamageAnchor,
  setRedeemFinishingByInstanceId,
  cancelUnitLethalDeathFx,
  prepareRedeemProjectiles,
  playRedeemProjectile,
  holdRedeemSpawnedUnits,
  releaseRedeemSpawnedUnits,
  playRedeemGrowPulse,
  playRedeemGeneralHeal,
}) {
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const seenSequenceIdsRef = useRef(new Set());
  const awaitingEffectSequenceRef = useRef(null);
  const effectWaitRef = useRef(null);
  // Redeem projectiles waiting to be fired, keyed by the redeemer's unit instanceId.
  const stashedProjectilesRef = useRef(new Map());
  // Units whose "damage taken" indicator has already been shown (fire once, at mark time).
  const damagePoppedInstanceIdsRef = useRef(new Set());
  const [isSequenceActive, setIsSequenceActive] = useState(false);

  // Keep the projectile callbacks in refs so the async pump never captures a stale closure.
  const prepareRedeemProjectilesRef = useRef(prepareRedeemProjectiles);
  prepareRedeemProjectilesRef.current = prepareRedeemProjectiles;
  const playRedeemProjectileRef = useRef(playRedeemProjectile);
  playRedeemProjectileRef.current = playRedeemProjectile;
  const holdRedeemSpawnedUnitsRef = useRef(holdRedeemSpawnedUnits);
  holdRedeemSpawnedUnitsRef.current = holdRedeemSpawnedUnits;
  const releaseRedeemSpawnedUnitsRef = useRef(releaseRedeemSpawnedUnits);
  releaseRedeemSpawnedUnitsRef.current = releaseRedeemSpawnedUnits;
  const playRedeemGrowPulseRef = useRef(playRedeemGrowPulse);
  playRedeemGrowPulseRef.current = playRedeemGrowPulse;
  const playRedeemGeneralHealRef = useRef(playRedeemGeneralHeal);
  playRedeemGeneralHealRef.current = playRedeemGeneralHeal;
  // Spawned-unit instanceIds held hidden per redeemer sequenceId, released when its finale ends.
  const heldSpawnsBySequenceRef = useRef(new Map());

  const clearEffectWait = useCallback(() => {
    const pending = effectWaitRef.current;
    if (!pending) {
      return;
    }
    if (pending.timeoutId != null) {
      window.clearTimeout(pending.timeoutId);
    }
    if (pending.postHitTimeoutId != null) {
      window.clearTimeout(pending.postHitTimeoutId);
    }
    effectWaitRef.current = null;
  }, []);

  const waitForRedeemEffect = useCallback((instanceId) => new Promise((resolve) => {
    clearEffectWait();
    const timeoutId = window.setTimeout(() => {
      if (effectWaitRef.current?.resolve !== resolve) {
        return;
      }
      clearEffectWait();
      resolve();
    }, REDEEM_DEATH_EFFECT_WAIT_MS);
    effectWaitRef.current = {
      instanceId,
      resolve,
      timeoutId,
      postHitTimeoutId: null,
    };
  }), [clearEffectWait]);

  const finishRedeemEffectWait = useCallback((instanceId) => {
    const pending = effectWaitRef.current;
    if (!pending || pending.instanceId !== instanceId || !pending.resolve) {
      return;
    }
    if (pending.timeoutId != null) {
      window.clearTimeout(pending.timeoutId);
    }
    const resolve = pending.resolve;
    pending.postHitTimeoutId = window.setTimeout(() => {
      if (effectWaitRef.current?.resolve !== resolve) {
        return;
      }
      clearEffectWait();
      resolve();
    }, REDEEM_DEATH_EFFECT_POST_HIT_MS);
  }, [clearEffectWait]);

  const clearMarkedGhost = useCallback((seq) => {
    if (!seq?.unitInstanceId) {
      throw new Error('clearMarkedGhost: unitInstanceId is required');
    }
    const slotKey = `${seq.viewerZoneName}_${seq.viewerIndex}`;
    redeemMarkedByInstanceIdRef.current.delete(seq.unitInstanceId);
    if (!completedRedeemDeathInstanceIdsRef?.current) {
      throw new Error('clearMarkedGhost: completedRedeemDeathInstanceIdsRef is required');
    }
    completedRedeemDeathInstanceIdsRef.current.add(seq.unitInstanceId);
    pendingUnitDamageFxSlotsRef?.current?.delete(slotKey);
    cancelUnitLethalDeathFx?.(seq.unitInstanceId, seq.viewerZoneName, seq.viewerIndex);
    clearSlotPreviousStateRef?.current?.(seq.viewerZoneName, seq.viewerIndex);
    setRedeemMarkedGhostTick((tick) => tick + 1);
  }, [
    redeemMarkedByInstanceIdRef,
    completedRedeemDeathInstanceIdsRef,
    pendingUnitDamageFxSlotsRef,
    setRedeemMarkedGhostTick,
    cancelUnitLethalDeathFx,
    clearSlotPreviousStateRef,
  ]);

  // Show the "damage taken" indicator for a dying redeem unit. Fired ONCE per unit at mark time
  // (when the authoritative broadcast first arrives) — the slot DOM still shows the live unit, so
  // the anchor resolves reliably. The killing effect's own popup is suppressed for redeem units
  // (their slot-death FX is skipped), so this is the single source of the damage indicator and
  // mirrors what a non-redeem unit shows the instant it takes lethal damage.
  const popRedeemDamageIndicator = useCallback((seq) => {
    if (damagePoppedInstanceIdsRef.current.has(seq.unitInstanceId)) {
      return;
    }
    damagePoppedInstanceIdsRef.current.add(seq.unitInstanceId);
    if (typeof resolveSlotDamageAnchor !== 'function' || typeof queueDamageAnimationTrigger !== 'function') {
      return;
    }
    const anchor = resolveSlotDamageAnchor(seq.viewerZoneName, seq.viewerIndex, seq.unitInstanceId);
    if (!anchor) {
      return;
    }
    const animationKey = `redeem-mark-${seq.unitInstanceId}-${Date.now()}`;
    queueDamageAnimationTrigger(animationKey, {
      attackId: animationKey,
      x: anchor.x,
      y: anchor.y,
      slotElement: anchor.slotElement,
      rect: anchor.rect,
      timestamp: Date.now(),
      targetZone: seq.viewerZoneName,
      targetIndex: seq.viewerIndex,
      targetInstanceId: seq.unitInstanceId,
      damageAmount: seq.markDamageAmount,
    });
  }, [resolveSlotDamageAnchor, queueDamageAnimationTrigger]);

  // Apply the marked-for-death visual (white fade + ghost image) to a single sequence.
  // Called for EVERY queued unit up front so simultaneous deaths all appear marked while
  // they wait their turn — the redeem effect + finale still run strictly one-by-one.
  const markSequenceForDeath = useCallback((seq) => {
    if (!seq?.unitInstanceId) {
      throw new Error('markSequenceForDeath: unitInstanceId is required');
    }
    const slotKey = `${seq.viewerZoneName}_${seq.viewerIndex}`;
    suppressSlotDeathInstanceIdsRef.current.add(seq.unitInstanceId);
    // Show the damage indicator immediately (before clearing slot tracking, while the live unit is
    // still rendered so the anchor resolves). Fires once per unit.
    popRedeemDamageIndicator(seq);
    clearSlotPreviousStateRef?.current?.(seq.viewerZoneName, seq.viewerIndex);
    cancelUnitLethalDeathFx?.(seq.unitInstanceId, seq.viewerZoneName, seq.viewerIndex);
    pendingUnitDamageFxSlotsRef?.current?.add(slotKey);
    redeemMarkedByInstanceIdRef.current.set(seq.unitInstanceId, {
      ghostUnit: {
        ...seq.ghostUnit,
        instanceId: seq.unitInstanceId,
        health: 0,
        _authoritativeHealth: 0,
        _redeemMarkedForDeath: true,
      },
      viewerZoneName: seq.viewerZoneName,
      viewerIndex: seq.viewerIndex,
      unitInstanceId: seq.unitInstanceId,
    });
    setRedeemMarkedGhostTick((tick) => tick + 1);
  }, [
    suppressSlotDeathInstanceIdsRef,
    popRedeemDamageIndicator,
    clearSlotPreviousStateRef,
    cancelUnitLethalDeathFx,
    pendingUnitDamageFxSlotsRef,
    redeemMarkedByInstanceIdRef,
    setRedeemMarkedGhostTick,
  ]);

  const runRedeemerDeathFinale = useCallback(async (seq) => {
    if (!seq?.unitInstanceId) {
      throw new Error('runRedeemerDeathFinale: unitInstanceId is required');
    }
    setRedeemFinishingByInstanceId((prev) => ({
      ...prev,
      [seq.unitInstanceId]: {
        zoneName: seq.viewerZoneName,
        index: seq.viewerIndex,
        instanceId: seq.unitInstanceId,
        startedAt: Date.now(),
      },
    }));
    await delay(REDEEM_DEATH_FINALE_MS);
    clearMarkedGhost(seq);
    // Death VFX done — now reveal any unit this redeemer spawned (e.g. 138 → 149), so the new unit
    // "appears" only after the full mark → effect → dissipation flow instead of popping in early.
    const heldSpawns = heldSpawnsBySequenceRef.current.get(seq.sequenceId);
    if (heldSpawns && heldSpawns.length > 0) {
      releaseRedeemSpawnedUnitsRef.current?.(heldSpawns);
    }
    heldSpawnsBySequenceRef.current.delete(seq.sequenceId);
    setRedeemFinishingByInstanceId((prev) => {
      const next = { ...prev };
      delete next[seq.unitInstanceId];
      return next;
    });
  }, [clearMarkedGhost, setRedeemFinishingByInstanceId]);

  // Fire the stashed redeem projectile(s) for this unit and wait for them to land. The target HP
  // hold (set up front at consume time) releases when the projectile's damage popup completes.
  const playRedeemEffect = useCallback(async (seq) => {
    if (seq.hiveSurgePulseFx) {
      const playGrow = playRedeemGrowPulseRef.current;
      if (typeof playGrow !== 'function') {
        throw new Error('useRedeemDeathSequence: playRedeemGrowPulse is required when hiveSurgePulseFx is set');
      }
      playGrow(seq.hiveSurgePulseFx);
    }

    if (seq.viralEchoHealFx) {
      const playHeal = playRedeemGeneralHealRef.current;
      if (typeof playHeal !== 'function') {
        throw new Error('useRedeemDeathSequence: playRedeemGeneralHeal is required when viralEchoHealFx is set');
      }
      playHeal(seq.viralEchoHealFx);
    }

    const projectiles = stashedProjectilesRef.current.get(seq.unitInstanceId) || [];
    stashedProjectilesRef.current.delete(seq.unitInstanceId);

    if (projectiles.length === 0) {
      // Redeem dealt no animated damage (no valid target). Hold a short beat then continue.
      await delay(REDEEM_DEATH_EFFECT_POST_HIT_MS);
      return;
    }

    awaitingEffectSequenceRef.current = seq.sequenceId;
    const play = playRedeemProjectileRef.current;
    if (typeof play !== 'function') {
      throw new Error('useRedeemDeathSequence: playRedeemProjectile is required');
    }
    projectiles.forEach((payload) => play(payload));
    await waitForRedeemEffect(seq.unitInstanceId);
    awaitingEffectSequenceRef.current = null;
  }, [waitForRedeemEffect]);

  const pumpQueue = useCallback(async () => {
    if (processingRef.current) {
      return;
    }
    const next = queueRef.current[0];
    if (!next) {
      setIsSequenceActive(false);
      return;
    }

    processingRef.current = true;
    setIsSequenceActive(true);

    try {
      if (!next.unitInstanceId) {
        throw new Error('useRedeemDeathSequence: queued sequence missing unitInstanceId');
      }

      // Idempotent refresh: the unit was already marked (and its damage indicator shown) at consume
      // time; re-assert the mark here in case anything cleared the slot state while it waited in the
      // queue. The damage indicator itself fires once per unit (guarded), so this won't repeat it.
      markSequenceForDeath(next);

      await delay(REDEEM_DEATH_MARK_MS);
      await playRedeemEffect(next);
      await runRedeemerDeathFinale(next);

      queueRef.current.shift();
    } finally {
      processingRef.current = false;
      if (queueRef.current.length > 0) {
        pumpQueue();
      } else {
        setIsSequenceActive(false);
      }
    }
  }, [
    markSequenceForDeath,
    playRedeemEffect,
    runRedeemerDeathFinale,
  ]);

  const consumePendingRedeemDeathSequences = useCallback((gameStateFragment) => {
    const viewerUserId = viewerUserIdRef?.current;
    if (!gameStateFragment?.pendingRedeemDeathSequences?.length || !viewerUserId) {
      return;
    }
    const pending = [...gameStateFragment.pendingRedeemDeathSequences];
    delete gameStateFragment.pendingRedeemDeathSequences;

    // Pull the redeem projectiles out of the drone-damage queue (and hold their target HP up front)
    // so they don't auto-play all at once — the pump fires them one unit at a time.
    const prepare = prepareRedeemProjectilesRef.current;
    const projectilesByInstanceId = typeof prepare === 'function'
      ? prepare(gameStateFragment)
      : new Map();

    pending.forEach((raw) => {
      if (!raw?.sequenceId || !raw?.unitInstanceId) {
        throw new Error('consumePendingRedeemDeathSequences: sequenceId and unitInstanceId are required');
      }
      if (seenSequenceIdsRef.current.has(raw.sequenceId)) {
        return;
      }
      seenSequenceIdsRef.current.add(raw.sequenceId);
      const seq = convertRedeemDeathSequenceForViewer(raw, viewerUserId);
      const projectiles = projectilesByInstanceId?.get?.(raw.unitInstanceId);
      if (Array.isArray(projectiles) && projectiles.length > 0) {
        stashedProjectilesRef.current.set(raw.unitInstanceId, projectiles);
      }
      // Hide any unit this redeemer spawns (already in the authoritative state) until this
      // sequence's finale; otherwise spawn-type redeems would show the new unit immediately.
      const hold = holdRedeemSpawnedUnitsRef.current;
      if (typeof hold === 'function') {
        const heldSpawns = hold(raw.unitInstanceId, gameStateFragment, {
          playerId: raw.playerId,
          zone: raw.zone,
          index: raw.index,
        });
        if (Array.isArray(heldSpawns) && heldSpawns.length > 0) {
          heldSpawnsBySequenceRef.current.set(raw.sequenceId, heldSpawns);
        }
      }
      // Mark every unit immediately (white fade + ghost) so simultaneous deaths all show
      // marked-for-death while they wait. The redeem effect + finale stay one-by-one in pumpQueue.
      markSequenceForDeath(seq);
      queueRef.current.push(seq);
    });

    pumpQueue();
  }, [
    viewerUserIdRef,
    pumpQueue,
    markSequenceForDeath,
  ]);

  const notifyRedeemDeployAnimationComplete = useCallback((deployUnitInstanceId) => {
    if (!awaitingEffectSequenceRef.current || !deployUnitInstanceId) {
      return;
    }
    const current = queueRef.current[0];
    if (!current || current.unitInstanceId !== deployUnitInstanceId) {
      return;
    }
    finishRedeemEffectWait(deployUnitInstanceId);
  }, [finishRedeemEffectWait]);

  const findMarkedEntryForSlot = useCallback((zoneName, index) => {
    for (const entry of redeemMarkedByInstanceIdRef.current.values()) {
      if (entry.viewerZoneName === zoneName && entry.viewerIndex === index) {
        return entry;
      }
    }
    return null;
  }, [redeemMarkedByInstanceIdRef]);

  const isRedeemMarkedInstance = useCallback((instanceId) => {
    void redeemMarkedByInstanceIdRef;
    if (!instanceId) {
      return false;
    }
    return redeemMarkedByInstanceIdRef.current.has(instanceId);
  }, [redeemMarkedByInstanceIdRef]);

  // Hard-stop the sequence (battle end / cleanup). Prevents stranded timers from animating or
  // mutating state after the game is over, and clears any unfired projectiles.
  const reset = useCallback(() => {
    clearEffectWait();
    queueRef.current = [];
    stashedProjectilesRef.current.clear();
    seenSequenceIdsRef.current.clear();
    damagePoppedInstanceIdsRef.current.clear();
    // Release any spawned units still held hidden so they don't get stranded off-screen.
    const release = releaseRedeemSpawnedUnitsRef.current;
    if (typeof release === 'function') {
      for (const heldSpawns of heldSpawnsBySequenceRef.current.values()) {
        if (Array.isArray(heldSpawns) && heldSpawns.length > 0) {
          release(heldSpawns);
        }
      }
    }
    heldSpawnsBySequenceRef.current.clear();
    awaitingEffectSequenceRef.current = null;
    processingRef.current = false;
    setIsSequenceActive(false);
  }, [clearEffectWait]);

  return {
    isSequenceActive,
    consumePendingRedeemDeathSequences,
    notifyRedeemDeployAnimationComplete,
    findMarkedEntryForSlot,
    isRedeemMarkedInstance,
    reset,
  };
}
